import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { Helmet } from 'react-helmet';
import * as R from 'ramda';
import { Box, Flex, Icon, Input } from '@cowsquare/design';
import { getQueryObject } from '../../../utils/getQueryObject';
import { getTagClause, getTitleClause } from '../../../utils/getWhereClause';
import { useAdsQuery, useFilterTags, usePagesBindingUrl, useSearchText } from '../../../utils/hooks';
import { Advertisements } from '../../Advertisements';
import { ArticleCardList } from '../../ArticleCardList';
import { FilterTags } from '../../FilterTags';
import { ArticleCardListLoading, TagListLoading } from '../../Loading';
import { NoResult } from '../../NoResult';
import { Section } from '../../Section';
import { Title } from '../../Title';

const PAGE_SIZE = 20;

const GET_MEDIA_RELESES = gql`
  query getMediaReleses($limit: Int, $offset: Int, $where: hkdutest_media_releases_bool_exp) {
    tags: hkdutest_media_releases_tags {
      name
    }

    total: hkdutest_media_releases_aggregate(where: $where) {
      aggregate {
        count
      }
    }

    list: hkdutest_media_releases(
      limit: $limit
      order_by: { created_at: desc }
      offset: $offset
      where: $where
    ) {
      id
      author {
        real_avatar
        name
        email
      }
      name
      content
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
  }
`;

const List = ({ location, uri }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const [tag, handleTagChange] = useFilterTags(queryObject, uri);
  const [searchText, handleSearch] = useSearchText(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const tagFilter = getTagClause(tag);
  const titleFilter = getTitleClause(searchText);
  const { data, loading } = useQuery(GET_MEDIA_RELESES, {
    variables: {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      where: { ...tagFilter, ...titleFilter },
    },
  });
  const list = R.propOr([], 'list', data);
  const tags = R.pipe(R.propOr([], 'tags'), R.pluck('name'), R.unless(R.isEmpty, R.prepend('All')))(data);
  const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);

  return (
    <Section>
      <Helmet>
        <title>Media Release - HKDU Public</title>
      </Helmet>
      <Advertisements ads={ads} />
      <Box px={[5, 0]} pt={8}>
        <Box mb={5}>
          <Flex flexDirection={['column', 'row']} justifyContent="space-between">
            <Title m={0} mb={[5, 0]}>
              Media Release
            </Title>
            <Box width={[1, '200px']} mt={[6, 0]}>
              <Input
                placeholder="Search"
                suffix={<Icon name="SEARCH" />}
                defaultValue={searchText}
                onChange={handleSearch}
              />
            </Box>
          </Flex>
        </Box>
        <Box mb={6}>
          {R.and(R.isEmpty(tags), loading) && <TagListLoading />}
          <FilterTags value={tag} tags={tags} onChange={handleTagChange} />
        </Box>
        <Box pb={8}>
          {R.and(R.isEmpty(list), !loading) && <NoResult>No result found...</NoResult>}
          {R.and(R.isEmpty(list), loading) && <ArticleCardListLoading />}
          <ArticleCardList uri={uri} page={page} onPageChange={onPageChange} list={list} total={total} />
        </Box>
      </Box>
    </Section>
  );
};

export default List;
